import { MAX_DIAGNOSIS_POINTERS_PER_LINE } from '@/model/middleware-types'
import type { BillingEntry } from './progressNoteEditSchema'

type Dx = NonNullable<BillingEntry['billingItems'][number]['dx']>

/** A doctor's edit of an encounter diagnosis in the Problem List (PD-2016). */
export type NoteDiagnosisChange =
  | { kind: 'rename'; from: string; to: Dx }
  | { kind: 'add'; dx: Dx }
  | { kind: 'remove'; code: string }

/**
 * PD-2016 — carry one Problem List edit onto the visit's billing lines, one way (billing
 * edits never touch the note). Only the edited code moves, so a diagnosis a coder added
 * or removed on the billing side stays as the coder left it. No billability filter: a
 * non-billable code reaches billing and is flagged there.
 *
 * - rename: every line's `from` becomes `to` at the same position; when no line carries
 *   `from`, it is an add of `to`.
 * - add: onto the primary line (the selected E/M candidate's line, else the first coded
 *   line) unless it is already there; `skipped` names that line when it already links the
 *   most diagnoses a claim line may carry.
 * - remove: dropped from every line. The caller emits it only when no other Problem List
 *   row still has the code.
 *
 * With no coded line at all nothing changes: the first save builds the line.
 */
export function applyNoteDiagnosisChange(
  entries: BillingEntry[],
  change: NoteDiagnosisChange,
  selectedCptCode: string | undefined,
): { entries: BillingEntry[]; skipped?: { reason: 'cap'; procedureCode: string } } {
  if (change.kind === 'remove') {
    if (!entries.some((e) => e.billingItems.some((b) => b.dx?.code === change.code))) {
      return { entries }
    }
    return {
      entries: entries.map((e) => ({
        ...e,
        billingItems: e.billingItems.filter((b) => b.dx?.code !== change.code),
      })),
    }
  }

  if (change.kind === 'rename') {
    const { from, to } = change
    if (!to.code || from === to.code) return { entries }
    if (!entries.some((e) => e.billingItems.some((b) => b.dx?.code === from))) {
      return applyNoteDiagnosisChange(entries, { kind: 'add', dx: to }, selectedCptCode)
    }
    return {
      entries: entries.map((e) => {
        if (!e.billingItems.some((b) => b.dx?.code === from)) return e
        // `to` takes the edited row's position; a row that already links `to` goes, so a
        // replacement never shifts the line's primary diagnosis.
        return {
          ...e,
          billingItems: e.billingItems
            .filter((b) => b.dx?.code !== to.code)
            .map((b) => (b.dx?.code === from ? { ...b, dx: to } : b)),
        }
      }),
    }
  }

  if (!change.dx.code) return { entries }
  const coded = entries.filter((e) => e.procedure?.code)
  const primary =
    (selectedCptCode && coded.find((e) => e.procedure?.code === selectedCptCode)) || coded[0]
  if (!primary || primary.billingItems.some((b) => b.dx?.code === change.dx.code)) {
    return { entries }
  }
  const linked = new Set(primary.billingItems.map((b) => b.dx?.code).filter(Boolean)).size
  if (linked >= MAX_DIAGNOSIS_POINTERS_PER_LINE) {
    return { entries, skipped: { reason: 'cap', procedureCode: primary.procedure?.code ?? '' } }
  }
  return {
    entries: entries.map((e) =>
      e === primary
        ? { ...e, billingItems: [...e.billingItems, { id: crypto.randomUUID(), dx: change.dx }] }
        : e,
    ),
  }
}
